import React, { useEffect, useRef } from 'react';

/*
  Dotted globe with clicks arcing into the tracker, as on xpnsr.tech.

  Brand and theme aware: colours come from the v2 tokens (--v2-glow for the
  arcs and the hub, --text for the dots), so the same component is orange on
  XPNSR and violet on LetsCampaign, in light and dark. Purely decorative:
  aria-hidden, pauses off screen, a single still frame with reduced motion.
*/

const DEG = Math.PI / 180;
// Rough continents as ellipses in lon/lat degrees: [lon, lat, rLon, rLat].
const LAND = [
  [-100, 47, 32, 20], [-112, 62, 26, 10], [-90, 17, 11, 9], [-78, 8, 5, 5],
  [-60, -14, 15, 22], [-68, -38, 8, 14], [-42, 72, 13, 8],
  [12, 50, 16, 10], [26, 60, 14, 8], [-4, 40, 6, 5], [30, 40, 9, 4],
  [18, 8, 20, 20], [24, -18, 12, 14], [46, 24, 9, 9], [47, -19, 3, 6],
  [92, 52, 46, 16], [80, 25, 11, 11], [105, 32, 16, 12], [108, 12, 10, 9],
  [115, -2, 14, 6], [138, 37, 4, 7], [128, 62, 22, 9], [134, -25, 17, 11], [172, -42, 3, 5],
];
const CITIES = [
  [40.7, -74], [34, -118], [43.7, -79.4], [19.4, -99.1], [-23.5, -46.6], [51.5, -0.1],
  [52.5, 13.4], [52.2, 21], [40.4, -3.7], [6.5, 3.4], [19, 72.8], [-6.2, 106.8],
  [35.7, 139.7], [37.5, 127], [-33.9, 151.2], [25.2, 55.3],
];

function vec(lat, lon) {
  return [Math.cos(lat * DEG) * Math.cos(lon * DEG), Math.sin(lat * DEG), Math.cos(lat * DEG) * Math.sin(lon * DEG)];
}
function isLand(lon, lat) {
  for (let i = 0; i < LAND.length; i += 1) {
    const [x, y, rx, ry] = LAND[i];
    const dx = (lon - x) / rx;
    const dy = (lat - y) / ry;
    if (dx * dx + dy * dy <= 1) return true;
  }
  return false;
}
function landPoints() {
  const pts = [];
  const n = 9000;
  const golden = Math.PI * (3 - Math.sqrt(5));
  for (let i = 0; i < n; i += 1) {
    const y = 1 - (i / (n - 1)) * 2;
    const r = Math.sqrt(1 - y * y);
    const th = golden * i;
    const x = Math.cos(th) * r;
    const z = Math.sin(th) * r;
    if (isLand(Math.atan2(z, x) / DEG, Math.asin(y) / DEG)) pts.push([x, y, z]);
  }
  return pts;
}
function slerp(a, b, t) {
  const d = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
  const om = Math.acos(d);
  const so = Math.sin(om) || 1;
  const k1 = Math.sin((1 - t) * om) / so;
  const k2 = Math.sin(t * om) / so;
  return [a[0] * k1 + b[0] * k2, a[1] * k1 + b[1] * k2, a[2] * k1 + b[2] * k2];
}
function readTokens() {
  const css = getComputedStyle(document.documentElement);
  const rgb = (name, fallback) => (css.getPropertyValue(name).trim() || fallback).split(/[\s,]+/).map(Number);
  return { glow: rgb('--v2-glow', '255 122 26'), text: rgb('--text', '230 235 240'), bad: rgb('--danger', '255 93 93') };
}

export default function SignalGlobe({ className = '', style }) {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas || /jsdom/i.test(navigator.userAgent)) return undefined;
    let ctx;
    try {
      ctx = canvas.getContext('2d');
    } catch (_e) {
      return undefined;
    }
    if (!ctx) return undefined;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const pts = landPoints();
    const hub = vec(50.1, 8.7);
    let tokens = readTokens();
    let arcs = [];
    let rot = -0.6;
    const tilt = 0.32;
    let w = 0;
    let h = 0;
    let rad = 0;
    let last = 0;
    let spawnAt = 0;
    let running = true;
    let raf = 0;

    const size = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      rad = Math.min(w, h) * 0.42;
    };
    const project = (v) => {
      const ca = Math.cos(rot);
      const sa = Math.sin(rot);
      const ct = Math.cos(tilt);
      const st = Math.sin(tilt);
      const x = v[0] * ca + v[2] * sa;
      const z = -v[0] * sa + v[2] * ca;
      return [w / 2 + rad * x, h / 2 - rad * (v[1] * ct - z * st), v[1] * st + z * ct];
    };
    const rgba = (c, a) => 'rgba(' + c[0] + ',' + c[1] + ',' + c[2] + ',' + a.toFixed(3) + ')';

    const frame = (ts) => {
      if (!running) return;
      const dt = last ? Math.min(50, ts - last) : 16;
      last = ts;
      if (!reduced) rot += dt * 0.00009;
      ctx.clearRect(0, 0, w, h);
      const { glow, text, bad } = tokens;

      ctx.strokeStyle = rgba(text, 0.08);
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.arc(w / 2, h / 2, rad, 0, Math.PI * 2);
      ctx.stroke();

      for (let i = 0; i < pts.length; i += 1) {
        const p = project(pts[i]);
        if (p[2] < -0.05) continue;
        ctx.fillStyle = rgba(text, 0.1 + 0.5 * Math.max(0, p[2]));
        ctx.fillRect(p[0], p[1], 1.6, 1.6);
      }

      const hp = project(hub);
      if (hp[2] > -0.2) {
        const pulse = (ts % 1800) / 1800;
        ctx.strokeStyle = rgba(glow, 0.6 * (1 - pulse));
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(hp[0], hp[1], 4 + pulse * 18, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = rgba(glow, 1);
        ctx.beginPath();
        ctx.arc(hp[0], hp[1], 3.2, 0, Math.PI * 2);
        ctx.fill();
      }

      if (!reduced && ts > spawnAt) {
        const c = CITIES[Math.floor(Math.random() * CITIES.length)];
        arcs.push({ a: vec(c[0], c[1]), t: 0, bot: Math.random() < 0.18 });
        spawnAt = ts + 520 + Math.random() * 500;
      }
      for (let j = arcs.length - 1; j >= 0; j -= 1) {
        const arc = arcs[j];
        arc.t += dt / 1700;
        const head = Math.min(1, arc.t);
        const tail = Math.max(0, arc.t - 0.45);
        const color = arc.bot ? bad : glow;
        let prev = null;
        for (let s = 0; s <= 28; s += 1) {
          const u = tail + (head - tail) * (s / 28);
          const v = slerp(arc.a, hub, u);
          const lift = 1 + 0.22 * Math.sin(Math.PI * u);
          const q = project([v[0] * lift, v[1] * lift, v[2] * lift]);
          if (prev && q[2] > -0.15 && prev[2] > -0.15) {
            ctx.strokeStyle = rgba(color, (s / 28) * 0.9);
            ctx.lineWidth = 1.6;
            ctx.beginPath();
            ctx.moveTo(prev[0], prev[1]);
            ctx.lineTo(q[0], q[1]);
            ctx.stroke();
          }
          prev = q;
        }
        if (arc.t < 1 && prev && prev[2] > -0.15) {
          ctx.fillStyle = rgba(color, 1);
          ctx.shadowColor = rgba(color, 1);
          ctx.shadowBlur = 12;
          ctx.beginPath();
          ctx.arc(prev[0], prev[1], 2.6, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }
        if (arc.t > 1.45) arcs.splice(j, 1);
      }
      raf = requestAnimationFrame(frame);
    };

    size();
    window.addEventListener('resize', size);
    // Theme switches change the tokens; pick them up.
    const mo = new MutationObserver(() => { tokens = readTokens(); });
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    let io;
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        const visible = entries[0].isIntersecting;
        if (visible && !running) {
          running = true;
          last = 0;
          raf = requestAnimationFrame(frame);
        }
        if (!visible) running = false;
      });
      io.observe(canvas);
    }
    raf = requestAnimationFrame(frame);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', size);
      mo.disconnect();
      if (io) io.disconnect();
      arcs = [];
    };
  }, []);

  return <canvas ref={ref} className={className} style={style} aria-hidden="true" />;
}
